import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Tooltip,
  Skeleton,
  CircularProgress,
  Pagination,
} from '@mui/material';

import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  BadgeOutlined as BadgeIcon,
  GroupOutlined as GroupIcon,
  AccountTreeOutlined as DeptIcon,
  InfoOutlined as InfoIcon,
  WarningAmberRounded as WarnIcon,
} from '@mui/icons-material';

import { successMessage } from '../Message';
import { delete_Role, LoadRoles, newRole, roleImpact, updateRole } from '../BaseURL';
import {
  AdminPageHead, AdminCard, EmptyState, PrimaryButton, GhostButton,
  adminFieldSx, ADMIN, BRAND,
} from './adminUi';

// ============================================================================
//  User Roles
// ============================================================================
//
//  This screen used to be a one-column table: thirty-odd rows, each holding a
//  single short string, stretched across a 1400px monitor with the other 90% of
//  every row empty. Finding "Pharmacy" meant scrolling past everything above
//  it, and the search box scrolled away with the page — so the moment you were
//  deep enough in the list to need it, it was gone.
//
//  It is now a tile grid: the same roles at three per row instead of one, which
//  turns thirty rows of scrolling into ten, with the search and filters pinned
//  above it. Each tile also carries what the old table left out — how many
//  users hold the role and how many departments it can reach — because "Acc/ph"
//  on its own tells an admin nothing about whether it is safe to delete.

// A role name is arbitrary free text, so there is no field to colour by. The
// tint is derived from the name itself: stable across reloads (the same role is
// always the same colour, which is what makes a grid scannable) without needing
// a column in the database to store it.
const TINTS = [
  { fg: BRAND.navy,    bg: BRAND.navySoft },
  { fg: BRAND.green,   bg: BRAND.greenSoft },
  { fg: BRAND.info,    bg: 'rgba(43,127,196,.10)' },
  { fg: BRAND.warning, bg: 'rgba(224,139,47,.10)' },
  { fg: '#7A5AF8',     bg: 'rgba(122,90,248,.10)' },
  { fg: '#0E926A',     bg: 'rgba(14,146,106,.10)' },
];

const tintFor = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 9973;
  return TINTS[h % TINTS.length];
};

// Up to two letters, skipping the punctuation these names are full of
// ("Acc/ph", "Doctor - In and outPatient").
const initialsFor = (name = '') => {
  const words = name.replace(/[^A-Za-z0-9 ]/g, ' ').split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

// `department` arrives from LoadRoles as a comma-joined list of department ids,
// empty string when the role has no access rows at all.
const deptCount = (role) =>
  String(role?.department || '').split(',').filter(Boolean).length;

const FILTERS = [
  { key: 'all',    label: 'All' },
  { key: 'inuse',  label: 'In use' },
  { key: 'unused', label: 'Unassigned' },
];

// 24 tiles is eight rows of the three-column grid — roughly one screen. The
// point of the page cap is that this screen's height stops depending on how
// many roles the facility happens to have: forty roles and four hundred both
// render the same one-screen page.
const PAGE_SIZE = 24;

function Role() {
  const [roles, setRoles]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [searchTermRole, setSearch] = useState('');
  const [filterKey, setFilterKey]   = useState('all');
  const [page, setPage]             = useState(1);

  const [openDialog, setOpenDialog]   = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData]       = useState({ name: '' });
  const [saving, setSaving]           = useState(false);

  // What renaming the role currently open in the dialog would carry with it.
  // Fetched when the dialog opens so the warning is on screen BEFORE the admin
  // types, not after they have saved.
  const [impact, setImpact] = useState(null);

  useEffect(() => { loadRoles(); }, []);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(LoadRoles);
      if (response.data.success) setRoles(response.data.results || []);
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to Load Data',
        icon: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Case-insensitive sort. The raw order put "cashier" between "Account
  // Officer" and "CLAIMS" because MySQL sorted by byte value, which made the
  // list look shuffled to anyone scanning it alphabetically.
  const visibleRoles = useMemo(() => {
    const q = searchTermRole.trim().toLowerCase();
    return roles
      .filter((r) => {
        // Guarded: a role row with a null name used to take the whole screen
        // down with "cannot read properties of null".
        const name = String(r?.role || '');
        if (q && !name.toLowerCase().includes(q)) return false;
        const users = Number(r?.user_count) || 0;
        if (filterKey === 'inuse')  return users > 0;
        if (filterKey === 'unused') return users === 0;
        return true;
      })
      .sort((a, b) =>
        String(a?.role || '').localeCompare(String(b?.role || ''), undefined, { sensitivity: 'base' })
      );
  }, [roles, searchTermRole, filterKey]);

  const totalPages = Math.max(1, Math.ceil(visibleRoles.length / PAGE_SIZE));

  // Deleting the last role on the final page, or narrowing a search, can leave
  // `page` past the end — which would render an empty grid over a list that is
  // not empty, looking exactly like a load failure.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageRoles = useMemo(
    () => visibleRoles.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [visibleRoles, page]
  );

  const totals = useMemo(() => {
    const assigned = roles.reduce((n, r) => n + (Number(r?.user_count) || 0), 0);
    const unused   = roles.filter((r) => !(Number(r?.user_count) || 0)).length;
    return { assigned, unused };
  }, [roles]);

  // ── Dialog ────────────────────────────────────────────────────────────────

  const handleOpenDialog = useCallback(async (role = null) => {
    setEditingRole(role);
    setFormData({ name: role ? role.role : '' });
    setImpact(null);
    setOpenDialog(true);

    if (role?.id) {
      try {
        const { data } = await axios.get(roleImpact(role.id));
        if (data?.success) setImpact(data.results);
      } catch (_) {
        // Advisory only — a failed impact lookup must not block the edit.
      }
    }
  }, []);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRole(null);
    setFormData({ name: '' });
    setImpact(null);
  };

  const trimmedName = formData.name.trim();

  // Caught in the field rather than on submit, so the admin sees it while
  // typing instead of after a round trip.
  const duplicateName = useMemo(() => {
    if (!trimmedName) return false;
    return roles.some(
      (r) =>
        r.id !== editingRole?.id &&
        String(r?.role || '').trim().toLowerCase() === trimmedName.toLowerCase()
    );
  }, [roles, trimmedName, editingRole]);

  const canSave = !!trimmedName && !duplicateName && !saving;

  const handleSubmit = async () => {
    if (!canSave) return;

    const renaming = !!editingRole && trimmedName !== editingRole.role;
    const carried  = (impact?.userCount || 0) + (impact?.departmentCount || 0);

    const result = await Swal.fire({
      title: editingRole ? 'Save changes?' : 'Create this role?',
      html: renaming && carried
        ? `Renaming <b>${editingRole.role}</b> to <b>${trimmedName}</b> will also update `
          + `<b>${impact.userCount}</b> user account(s) and `
          + `<b>${impact.departmentCount}</b> department permission(s).`
        : `Role: <b>${trimmedName}</b>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: BRAND.green,
      cancelButtonColor: BRAND.inkMuted,
      confirmButtonText: editingRole ? 'Save changes' : 'Create role',
    });

    if (!result.isConfirmed) return;

    setSaving(true);
    try {
      const response = editingRole
        ? await axios.put(updateRole(editingRole.id), { name: trimmedName })
        : await axios.post(newRole, { name: trimmedName });

      if (response.data.success) {
        successMessage(editingRole ? 'Updated' : 'Created', response.data.message);
        // Closed on BOTH paths. Creating a role previously left the dialog
        // open with the field cleared, which reads as "nothing happened" and
        // is how the same role ends up added three times.
        handleCloseDialog();
        loadRoles();
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to Save',
        icon: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDeleteRole = async (role) => {
    // Ask what the delete would break before offering it. The server refuses a
    // role that still has users, but finding that out from a red error box
    // after clicking "Yes, delete" is a worse experience than being told up
    // front — and being told WHO is holding it.
    let info = null;
    try {
      const { data } = await axios.get(roleImpact(role.id));
      if (data?.success) info = data.results;
    } catch (_) { /* fall through to a plain confirm */ }

    if (info && info.userCount > 0) {
      const names = (info.sampleUsers || []).map((u) => u.name || u.username).filter(Boolean);
      await Swal.fire({
        title: 'Role is in use',
        icon: 'warning',
        html:
          `<b>${role.role}</b> is assigned to <b>${info.userCount}</b> user account(s), `
          + `so it cannot be deleted yet.`
          + (names.length
              ? `<br/><br/><span style="font-size:13px;color:${BRAND.inkMuted}">`
                + `${names.join(', ')}${info.userCount > names.length ? ` and ${info.userCount - names.length} more` : ''}`
                + `</span>`
              : '')
          + `<br/><br/><span style="font-size:13px">Move them to another role on the `
          + `<b>System Users</b> screen first.</span>`,
        confirmButtonColor: BRAND.navy,
        confirmButtonText: 'Got it',
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Delete this role?',
      html: `<b>${role.role}</b> will be removed`
        + (info?.departmentCount
            ? `, along with its <b>${info.departmentCount}</b> department permission(s).`
            : '.')
        + `<br/><br/><span style="font-size:13px;color:${BRAND.inkMuted}">This cannot be undone.</span>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: BRAND.danger,
      cancelButtonColor: BRAND.inkMuted,
      confirmButtonText: 'Yes, delete it',
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: 'Deleting…',
      text: 'Please wait',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const response = await axios.delete(delete_Role(role.id));
      if (response.data.success) {
        successMessage('Deleted', response.data.message);
        loadRoles();
      }
    } catch (error) {
      await Swal.fire({
        title: 'Could not delete',
        text: error.response?.data?.message || 'Failed to delete role',
        icon: 'error',
      });
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const searchActive = !!searchTermRole.trim() || filterKey !== 'all';

  return (
    <Box>
      <AdminPageHead
        icon={BadgeIcon}
        title="User Roles"
        subtitle="The job titles staff are assigned to. A role decides which departments and screens its holders can reach."
        actions={
          <PrimaryButton
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ width: { xs: '100%', sm: 'auto' }, whiteSpace: 'nowrap' }}
          >
            New Role
          </PrimaryButton>
        }
      />

      <AdminCard>
        {/* ── Toolbar ──────────────────────────────────────────────────────
            Deliberately NOT position:sticky. It was, briefly, to keep the
            search box reachable from the bottom of a long list — but it never
            actually pinned. AdminPanel's content area carries overflowX:'auto'
            (for the unconverted screens with wide tables), and `overflow-x:auto`
            computes `overflow-y` to `auto` as well. That makes the content area
            a scroll container, so it — not the window — becomes the scrollport
            a sticky descendant resolves against. It has no constrained height
            and therefore never scrolls, so the toolbar sat in flow and scrolled
            away exactly as before, just with dead CSS attached.

            Verified in Chrome, not assumed: same markup sticks at the requested
            offset without the overflow ancestor and does not stick with it.

            The list is paginated instead, which is the better fix anyway — the
            toolbar barely leaves the viewport when the page is one screen tall,
            and it solves the scrolling complaint rather than working around it. */}
        <Box
          sx={{
            pb: 1.75,
            mb: 2,
            borderBottom: `1px solid ${BRAND.line}`,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 1.5,
          }}
        >
          <TextField
            placeholder="Search roles…"
            value={searchTermRole}
            size="small"
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            sx={{ ...adminFieldSx, flex: 1, minWidth: 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 19, color: BRAND.inkFaint }} />
                </InputAdornment>
              ),
              endAdornment: searchTermRole ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setSearch(''); setPage(1); }} aria-label="Clear search">
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* Segmented filter. "Unassigned" is the one that earns its place —
              it is the only way to find roles nobody holds, which is most of
              what accumulates in a list like this. */}
          <Box
            sx={{
              display: 'flex',
              flexShrink: 0,
              p: .375,
              gap: .25,
              borderRadius: ADMIN.radiusSm,
              background: BRAND.canvas,
              border: `1px solid ${BRAND.line}`,
            }}
          >
            {FILTERS.map((f) => {
              const on = filterKey === f.key;
              const count = f.key === 'all'
                ? roles.length
                : f.key === 'unused' ? totals.unused : roles.length - totals.unused;
              return (
                <Box
                  key={f.key}
                  component="button"
                  type="button"
                  onClick={() => { setFilterKey(f.key); setPage(1); }}
                  aria-pressed={on}
                  sx={{
                    flex: { xs: 1, md: 'none' },
                    px: 1.5, py: .75,
                    border: 0, cursor: 'pointer', font: 'inherit',
                    borderRadius: '7px',
                    whiteSpace: 'nowrap',
                    background: on ? BRAND.surface : 'transparent',
                    boxShadow: on ? '0 1px 3px rgba(15,31,61,.12)' : 'none',
                    color: on ? BRAND.navy : BRAND.inkMuted,
                    fontSize: 12.5,
                    fontWeight: on ? 800 : 600,
                    transition: 'background .15s ease',
                    '&:hover': { color: BRAND.navy },
                  }}
                >
                  {f.label} · {count}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* ── Grid ───────────────────────────────────────────────────────── */}
        {loading ? (
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: {
                xs: 'minmax(0,1fr)',
                sm: 'repeat(2, minmax(0,1fr))',
                lg: 'repeat(3, minmax(0,1fr))',
              },
            }}
          >
            {Array.from({ length: 9 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={68} sx={{ borderRadius: ADMIN.radiusSm }} />
            ))}
          </Box>
        ) : visibleRoles.length === 0 ? (
          <EmptyState
            icon={searchActive ? SearchIcon : BadgeIcon}
            title={searchActive ? 'No roles match' : 'No roles yet'}
            body={
              searchActive
                ? 'Nothing here matches the current search and filter. Try a shorter term, or switch the filter back to All.'
                : 'Roles are the job titles you assign staff to. Create the first one and it becomes selectable when registering a user.'
            }
            action={
              searchActive ? (
                <GhostButton onClick={() => { setSearch(''); setFilterKey('all'); setPage(1); }}>
                  Clear search
                </GhostButton>
              ) : (
                <PrimaryButton startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                  New Role
                </PrimaryButton>
              )
            }
          />
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              // CSS Grid, not MUI <Grid>. In MUI 7 the `<Grid item xs md>` API
              // is silently ignored — every tile would render full width and
              // this screen would be right back to one long column.
              gridTemplateColumns: {
                xs: 'minmax(0,1fr)',
                sm: 'repeat(2, minmax(0,1fr))',
                lg: 'repeat(3, minmax(0,1fr))',
              },
            }}
          >
            {pageRoles.map((role) => {
              const tint  = tintFor(role.role);
              const users = Number(role.user_count) || 0;
              const depts = deptCount(role);

              return (
                <Box
                  key={role.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.5, py: 1.375,
                    minWidth: 0,
                    borderRadius: ADMIN.radiusSm,
                    border: `1px solid ${BRAND.line}`,
                    background: BRAND.surface,
                    transition: 'border-color .15s ease, box-shadow .15s ease',
                    '&:hover': {
                      borderColor: BRAND.navyLight,
                      boxShadow: ADMIN.shadow,
                    },
                    '&:hover .roleActions': { opacity: 1 },
                  }}
                >
                  <Box
                    sx={{
                      width: 38, height: 38, flexShrink: 0,
                      borderRadius: ADMIN.radiusSm,
                      display: 'grid', placeItems: 'center',
                      background: tint.bg,
                      color: tint.fg,
                      fontSize: 12.5, fontWeight: 800, letterSpacing: '.3px',
                    }}
                  >
                    {initialsFor(role.role)}
                  </Box>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Tooltip title={role.role} enterDelay={600}>
                      <Typography
                        noWrap
                        sx={{ fontSize: 13.5, fontWeight: 700, color: BRAND.ink, lineHeight: 1.35 }}
                      >
                        {role.role}
                      </Typography>
                    </Tooltip>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: .375 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: .375 }}>
                        <GroupIcon sx={{ fontSize: 13, color: users ? BRAND.inkFaint : '#C7D2E0' }} />
                        <Typography
                          sx={{
                            fontSize: 11.5, fontWeight: 600,
                            color: users ? BRAND.inkMuted : BRAND.inkFaint,
                          }}
                        >
                          {users === 0 ? 'no users' : `${users} user${users === 1 ? '' : 's'}`}
                        </Typography>
                      </Box>

                      <Box sx={{ width: 1, height: 11, background: BRAND.line }} />

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: .375 }}>
                        <DeptIcon sx={{ fontSize: 13, color: depts ? BRAND.inkFaint : '#C7D2E0' }} />
                        <Typography
                          sx={{
                            fontSize: 11.5, fontWeight: 600,
                            color: depts ? BRAND.inkMuted : BRAND.inkFaint,
                          }}
                        >
                          {depts === 0 ? 'no access' : `${depts} dept${depts === 1 ? '' : 's'}`}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box
                    className="roleActions"
                    sx={{
                      display: 'flex', gap: .25, flexShrink: 0,
                      opacity: { xs: 1, md: .45 },
                      transition: 'opacity .15s ease',
                      // Dimmed until hover on a mouse, but a touch screen never
                      // hovers — there the buttons stay fully visible or they
                      // are simply unreachable.
                      '@media (hover: none)': { opacity: 1 },
                    }}
                  >
                    <Tooltip title="Rename role">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(role)}
                        sx={{ color: BRAND.navy, '&:hover': { background: BRAND.navySoft } }}
                      >
                        <EditIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={users ? `In use by ${users} user${users === 1 ? '' : 's'}` : 'Delete role'}>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteRole(role)}
                        sx={{
                          color: users ? BRAND.inkFaint : BRAND.danger,
                          '&:hover': { background: users ? 'rgba(15,31,61,.05)' : 'rgba(214,69,69,.08)' },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Footer — confirms the filter is doing something when a search
            narrows thirty roles down to two, and pages the rest. */}
        {!loading && visibleRoles.length > 0 && (
          <Box
            sx={{
              mt: 2.25, pt: 2,
              borderTop: `1px solid ${BRAND.line}`,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.25,
            }}
          >
            <Typography sx={{ fontSize: 11.5, color: BRAND.inkFaint, fontWeight: 600 }}>
              {totalPages > 1
                ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, visibleRoles.length)} of ${visibleRoles.length}`
                : `Showing ${visibleRoles.length} of ${roles.length} role${roles.length === 1 ? '' : 's'}`}
              {totals.assigned
                ? ` · ${totals.assigned} user account${totals.assigned === 1 ? '' : 's'} assigned`
                : ''}
            </Typography>

            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, p) => setPage(p)}
                size="small"
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: 12.5, fontWeight: 600, color: BRAND.inkMuted,
                  },
                  '& .Mui-selected': {
                    background: `${BRAND.navy} !important`,
                    color: '#fff',
                    fontWeight: 800,
                  },
                }}
              />
            )}
          </Box>
        )}
      </AdminCard>

      {/* ── Add / rename dialog ──────────────────────────────────────────── */}
      <Dialog
        open={openDialog}
        onClose={saving ? undefined : handleCloseDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: ADMIN.radius, m: { xs: 2, sm: 4 } } }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2.5, sm: 3 }, py: 2.25,
            background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.navyDark} 100%)`,
            color: '#fff',
            display: 'flex', alignItems: 'center', gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 34, height: 34, borderRadius: ADMIN.radiusSm, flexShrink: 0,
              background: 'rgba(255,255,255,.14)', display: 'grid', placeItems: 'center',
            }}
          >
            <BadgeIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.3 }}>
              {editingRole ? 'Rename role' : 'New role'}
            </Typography>
            <Typography noWrap sx={{ fontSize: 12, opacity: .82, mt: .125 }}>
              {editingRole ? editingRole.role : 'Give the role a name staff will recognise'}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseDialog}
            disabled={saving}
            aria-label="Close"
            sx={{ color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,.18)' } }}
          >
            <CloseIcon sx={{ fontSize: 19 }} />
          </IconButton>
        </DialogTitle>

        {/* The top padding lives on an inner Box, NOT on DialogContent's own sx.
            MUI ships `.MuiDialogTitle-root + & { padding-top: 0 }` for a
            DialogContent that follows a DialogTitle, and that selector is two
            classes to the sx prop's one — so `pt` on DialogContent is silently
            discarded (measured: 0px). That is what pushed the field's floating
            label up under the navy header and sliced it in half. Padding on a
            child is not in that fight at all. */}
        <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, pb: 1 }}>
          <Box sx={{ pt: 2.75 }}>
            {/* A standing label rather than MUI's floating one. The floating
                label overlaps the top border of its own input, so in a dialog
                whose first element sits right below a coloured header it has
                nowhere safe to go — and it is the piece that got clipped.
                Above the field it cannot collide with anything. */}
            <Typography
              component="label"
              htmlFor="role-name-input"
              sx={{
                display: 'block', mb: .875,
                fontSize: 12, fontWeight: 700,
                color: BRAND.ink, letterSpacing: '.2px',
              }}
            >
              Role name <Box component="span" sx={{ color: BRAND.danger }}>*</Box>
            </Typography>

            <TextField
              id="role-name-input"
              value={formData.name}
              onChange={(e) => setFormData({ name: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter' && canSave) handleSubmit(); }}
              placeholder="e.g. Pharmacy, Account Officer, Ward Nurse"
              required
              fullWidth
              autoFocus
              error={duplicateName}
              helperText={
                duplicateName
                  ? 'A role with this name already exists'
                  : 'The name staff will see when they are assigned this role'
              }
              sx={{ ...adminFieldSx, '& .MuiFormHelperText-root': { mx: 0, mt: .75 } }}
            />
          </Box>

          {/* Rename consequences, stated before the fact. staff.role stores the
              role NAME, so a rename is not a cosmetic edit — it rewrites every
              account holding it. */}
          {editingRole && impact && (impact.userCount > 0 || impact.departmentCount > 0) && (
            <Box
              sx={{
                mt: 2, px: 1.5, py: 1.25,
                display: 'flex', gap: 1.25,
                borderRadius: ADMIN.radiusSm,
                background: trimmedName && trimmedName !== editingRole.role
                  ? 'rgba(224,139,47,.09)'
                  : BRAND.navySoft,
                border: `1px solid ${trimmedName && trimmedName !== editingRole.role
                  ? 'rgba(224,139,47,.28)' : BRAND.line}`,
              }}
            >
              {trimmedName && trimmedName !== editingRole.role
                ? <WarnIcon sx={{ fontSize: 17, color: BRAND.warning, flexShrink: 0, mt: .125 }} />
                : <InfoIcon sx={{ fontSize: 17, color: BRAND.navy, flexShrink: 0, mt: .125 }} />}
              <Typography sx={{ fontSize: 12, color: BRAND.ink, lineHeight: 1.55 }}>
                {impact.userCount > 0 && (
                  <>
                    <b>{impact.userCount}</b> user account{impact.userCount === 1 ? '' : 's'} hold
                    {impact.userCount === 1 ? 's' : ''} this role
                  </>
                )}
                {impact.userCount > 0 && impact.departmentCount > 0 && ' and '}
                {impact.departmentCount > 0 && (
                  <>
                    <b>{impact.departmentCount}</b> department permission
                    {impact.departmentCount === 1 ? '' : 's'} reference{impact.departmentCount === 1 ? 's' : ''} it
                  </>
                )}
                . {trimmedName && trimmedName !== editingRole.role
                    ? 'They will all be updated to the new name.'
                    : 'Renaming updates them all automatically.'}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2.5, sm: 3 }, py: 2.25, gap: 1.25,
            borderTop: `1px solid ${BRAND.line}`,
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            '& > *': { width: { xs: '100%', sm: 'auto' }, ml: { xs: '0 !important', sm: undefined } },
          }}
        >
          <GhostButton onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </GhostButton>
          <PrimaryButton
            onClick={handleSubmit}
            disabled={!canSave}
            startIcon={saving
              ? <CircularProgress size={15} sx={{ color: '#fff' }} />
              : <SaveIcon sx={{ fontSize: 17 }} />}
          >
            {saving ? 'Saving…' : editingRole ? 'Save changes' : 'Create role'}
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Role;
